import { useState } from 'react';
import { SelectOption } from '../searchableSelect/searchableSelectTypes';
import { getTranslationFromTranslatableEntity } from '../../constants/matter';
import { displayLanguageName } from '../../constants/utilityFunctions';
import { useLanguage } from '../../context/LanguageContext';
import { useManageMatterRequestContext } from '../../context/ManageMatterRequest';
import { useFetchLanguages } from '../../hooks/useFetchLanguages';
import { ILanguage } from '../../interface/languages';
import { IMatterRequest } from '../../interface/matterRequest';

/** The props `SearchableSelect` needs to pick a matter request's language, from the same list the picker screen uses. */
export const useMatterRequestLanguageSelect = () => {
  const { matterRequest, updateManageMatterRequest } = useManageMatterRequestContext();
  const { language: appLanguage } = useLanguage();
  const [searchValue, setSearchValue] = useState('');
  const {
    data: languages,
    isLoading,
    isFetchingNextPage,
    hasNextPage,
    fetchNextPage,
  } = useFetchLanguages({ pageSize: 100, search: searchValue });

  const options: SelectOption[] = languages
    .map((language: ILanguage) => ({
      value: language.languageCode,
      label: getTranslationFromTranslatableEntity(language.translations, appLanguage),
    }))
    .sort((a: SelectOption, b: SelectOption) => a.label.localeCompare(b.label, appLanguage));
  const value: SelectOption | null = matterRequest?.language
    ? {
        value: matterRequest.language,
        label: displayLanguageName(matterRequest.language, appLanguage),
      }
    : null;

  const onChange = (option: SelectOption) => {
    const language = languages.find(({ languageCode }: ILanguage) => languageCode === option.value);
    if (!language) return;
    updateManageMatterRequest({
      ...matterRequest,
      language: language.languageCode,
      isLanguageSetByUser: true,
    } as unknown as IMatterRequest);
  };

  return {
    options,
    value,
    onChange,
    onSearch: setSearchValue,
    onLoadMore: fetchNextPage,
    hasMore: Boolean(hasNextPage),
    loading: isLoading,
    loadingMore: isFetchingNextPage,
  };
};
